import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import service from '../../service';
import { useMessages } from '../../contexts/messages';
import { DateTime } from 'luxon';
import {
  toastError,
  toastSuccess,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';
import ModalLg from '../ModalLg';
import { useModal } from '../../contexts/modal';
import OutboundMessageForm from '../OutboundMessageForm';
import PreviewEmailHtml from '../PreviewEmailHtml';
import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import { Input } from '@art-of-hospitality-solutions-llc/ui-kit/components/input';
import { Separator } from '@art-of-hospitality-solutions-llc/ui-kit/components/separator';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/tabs';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from '../Card/index';
import {
  DataTable,
  DataTablePagination,
  defineTableSchema,
  useConfiguredTable,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/data-table';
import {
  AddFilterTrigger,
  FilterBar,
  FilterEditor,
  useFilters,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/filters';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbSeparator,
} from '../Breadcrumb/index';
import ShowFor from '@Components/ShowFor';
import { getMemberProfile } from "../../utils/fish";

const NOT_AVAILABLE = 'N/A';
const FALLBACK_DATA = [];

const getMainSubscription = (fish) => {
  const subscriptions = fish?.subscriptions;
  if (subscriptions) {
    return subscriptions[0];
  }

  return {};
};

const getSubscriptionStatus = (row) => {
  const subscription = getMainSubscription(row);
  if (!subscription) {
    return 'Non-member';
  } else if (!subscription.cancelledAt) {
    return 'Active';
  } else {
    const now = DateTime.now();
    const renewalDate = subscription.nextSubscriptionRenewal
      ? DateTime.fromISO(subscription.nextSubscriptionRenewal)
      : null;
    if (renewalDate && now < renewalDate) return 'Cancelled';
    else return 'Expired';
  }
};

const getBestEffortProfileValue = (fish, property) => {
  if (!fish) return NOT_AVAILABLE;

  let profile = getMemberProfile(fish) || fish.fishProfiles[0];

  if (!profile) {
    return NOT_AVAILABLE;
  }

  let prop = profile[property] ?? null;
  if (!prop) {
    prop = NOT_AVAILABLE;
  }

  return prop;
};

function formatPhoneNumber(input) {
  const digits = input.replace(/\D/g, '');
  if (digits.length === 10) {
    return digits.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
  }
  return input;
}

export const DEFAULT_CONTACT_CONFIG = [
  {
    id: 'firstName',
    headerLabel: 'First Name',
    accessor: (row) => getBestEffortProfileValue(row, 'fName'),
    type: 'text',
    size: 120,
  },
  {
    id: 'lastName',
    headerLabel: 'Last Name',
    accessor: (row) => getBestEffortProfileValue(row, 'lName'),
    type: 'text',
    size: 120,
  },
  {
    id: 'email',
    headerLabel: 'Email',
    accessor: (row) => getBestEffortProfileValue(row, 'email'),
    type: 'text',
    size: 220,
  },
  {
    id: 'phoneNumber',
    headerLabel: 'Phone Number',
    accessor: (row) => row.phoneNumber ?? '',
    type: 'text',
    cell: (info) => {
      const value = info.getValue();
      return value ? formatPhoneNumber(value) : 'N/A';
    },
  },
];

const CampaignDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [messageRecipients, setMessageRecipients] = useState([]);
  const [formattedRecipients, setFormattedRecipients] = useState([]);
  const [senderInbox, setSenderInbox] = useState({});
  const [messageSegments, setMessageSegments] = useState([]);
  const { segments } = useMessages();

  const [defaultTab, setDefaultTab] = useState(null);

  const { openModal, closeModal } = useModal();
  const [modalContent, setModalContent] = useState(null);

  const { filters, addOrUpdateFilter, removeFilter } = useFilters([]);

  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const columnConfigs = DEFAULT_CONTACT_CONFIG;
  const [columnPinning, setColumnPinning] = useState({
    left: ['select', 'firstName', 'lastName'],
    right: [],
  });

  const { columns, filters: filterDefinitions } =
    defineTableSchema(columnConfigs);

  const sortBy = 'lastName';

  const { table } = useConfiguredTable({
    data: messageRecipients ?? FALLBACK_DATA,
    columns,
    filters,
    initialSorting: [{ id: sortBy, desc: true }],
    pagination: {
      pageIndex,
      pageSize,
      setPageIndex,
      setPageSize,
    },
    columnPinning,
    setColumnPinning,
    autoResetPageOnFilterChange: true,
  });

  const fetchCampaign = async (campaignId) => {
    const res = await service.readOutboundMessage(campaignId);
    if (!res.ok) return;
    const campaignRes = await res.json();
    setCampaign(campaignRes);
  };

  const getMessageSegments = (message) => {
    if (
      !message ||
      !message.segmentIds ||
      !Array.isArray(message.segmentIds) ||
      !segments
    ) {
      return [];
    }

    return message.segmentIds
      .map((segmentId) => segments.find((segment) => segment.id === segmentId))
      .filter((segment) => segment !== undefined);
  };

  const fetchMessageRecipients = async (messageId) => {
    try {
      const recipients = await service.getOutboundMessageRecipients(messageId);

      const formattedRecipients = recipients.map((recipient) => {
        const profile =
          recipient?.fishProfiles && recipient?.fishProfiles.length > 0
            ? recipient.fishProfiles[0]
            : {};
        return campaign?.method === 'EMAIL'
          ? {
              value: recipient.id,
              label: `${profile.fName || ''} ${profile.lName || ''} (${
                profile.email || 'No email'
              })`.trim(),
            }
          : {
              value: recipient.id,
              label: `${profile.fName || ''} ${profile.lName || ''} (${
                recipient.phoneNumber || 'No phone number'
              })`.trim(),
            };
      });

      setMessageRecipients(recipients);
      setFormattedRecipients(formattedRecipients);

      return formattedRecipients;
    } catch (error) {
      console.error('Error fetching message recipients:', error);
      setMessageRecipients([]);
    }
  };

  const getSenderInfo = async () => {
    if (campaign?.senderId) {
      const newSenderInbox = await service.readInbox(campaign?.senderId);
      if (newSenderInbox) {
        setSenderInbox(newSenderInbox);
      }
    }
  };

  const handleShowEditor = async () => {
    setModalContent('edit');
    openModal();
  };

  const handleSubmitEdits = async (data) => {
    if (data.sendNow) {
      // reset the sendTime to reflect 'now' (when the send/update button was pressed)
      data.sendTime = DateTime.local();
    }
    delete data.sendNow;

    const result = await service
      .updateOutboundMessage(campaign.id, data)
      .then(() => {
        toastSuccess('Message updated successfully!');
        closeModal();
      })
      .catch((error) => {
        console.error(error);
        toastError('Failed to update the message.');
      });

    fetchCampaign(id);
    fetchMessageRecipients(id);

    return result;
  };

  const handleDeleteSelected = () => {
    setModalContent('delete');
    openModal();
  };

  const confirmDelete = async () => {
    await service.deleteOutboundMessages([campaign.id]);
    closeModal();
    toastSuccess('Message/s deleted successfully!');

    navigate('/campaigns');
  };

  useEffect(() => {
    fetchCampaign(id);
    fetchMessageRecipients(id);
  }, [id]);

  useEffect(() => {
    if (campaign) {
      setDefaultTab(
        campaign?.method === 'EMAIL' && campaign?.html ? 'html' : 'plainText',
      );
    }
    const messageSegmentsRes = getMessageSegments(campaign);
    setMessageSegments(messageSegmentsRes);

    getSenderInfo();
  }, [campaign]);

  return (
    <div>
      <div className="font-questrial mb-4 flex items-center">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem
              onClick={() => {
                navigate('/dashboard');
              }}
              className={`cursor-pointer opacity-50`}
            >
              Dashboard
            </BreadcrumbItem>
            <BreadcrumbSeparator></BreadcrumbSeparator>
            <BreadcrumbItem
              onClick={() => {
                const previousTab = !campaign?.sentAt ? 'Scheduled' : 'History';
                navigate(`/campaigns`, {
                  state: {
                    loadTab: previousTab,
                  },
                });
              }}
              className={`cursor-pointer opacity-50`}
            >
              Campaigns
            </BreadcrumbItem>
            <BreadcrumbSeparator></BreadcrumbSeparator>
            <BreadcrumbItem className={`cursor-pointer`}>
              Campaign Details
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </div>

      <div className="flex w-full justify-between">
        <h1 className="mb-2 text-2xl font-bold">Campaign Details</h1>

        {campaign?.sentAt === null && (
          <ShowFor permissions={['CAMPAIGNS_WRITE']}>
            <div className="mb-4 flex w-fit gap-4">
              <Button onClick={handleShowEditor}>Edit</Button>

              <Button
                onClick={handleDeleteSelected}
                className="bg-gray-300 hover:bg-red-500 hover:text-white active:bg-red-600 active:text-white"
              >
                Delete
              </Button>
            </div>
          </ShowFor>
        )}
      </div>

      <div className="my-4">
        <Card>
          <CardHeader>
            <CardTitle>Title: {campaign?.campaignTitle || 'N/A'}</CardTitle>
            <CardDescription>
              Channel: {campaign?.method || 'N/A'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex">
              <div className="mx-1 flex w-1/2 flex-col gap-1">
                <span>Sender Name: {senderInbox?.user?.name || 'N/A'}</span>
                <Separator />
                <span>Send Attempts: {campaign?.numSendAttempts}</span>
              </div>
              <div className="mx-1 flex w-1/2 flex-col gap-1">
                {campaign?.sentAt && (
                  <>
                    <span>
                      Sent At:{' '}
                      {campaign?.sentAt
                        ? DateTime.fromISO(campaign?.sentAt).toFormat(
                            'MMM dd, yyyy h:mm a',
                          )
                        : 'N/A'}
                    </span>
                    <Separator />
                  </>
                )}

                {!campaign?.sentAt && (
                  <>
                    <span>
                      Send Time:{' '}
                      {campaign?.sendTime
                        ? DateTime.fromISO(campaign?.sendTime).toFormat(
                            'MMM dd, yyyy h:mm a',
                          )
                        : 'N/A'}
                    </span>
                    <Separator />
                  </>
                )}

                <span>
                  Create At:{' '}
                  {campaign?.createdAt
                    ? DateTime.fromISO(campaign?.createdAt).toFormat(
                        'MMM dd, yyyy h:mm a',
                      )
                    : 'N/A'}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="my-4">
        <div className="">
          <h1 className="mb-2 text-2xl font-bold">Message</h1>
        </div>
        <Card>
          {campaign?.messageSubject && (
            <CardHeader>
              <CardTitle>
                Subject: {campaign?.messageSubject || 'N/A'}
              </CardTitle>
            </CardHeader>
          )}
          <CardContent>
            {defaultTab && (
              <div>
                <Tabs defaultValue={defaultTab}>
                  <TabsList>
                    <TabsTrigger value="plainText">Message Body</TabsTrigger>
                    {campaign?.method === 'EMAIL' && (
                      <TabsTrigger value="html">HTML</TabsTrigger>
                    )}
                  </TabsList>
                  <TabsContent value="plainText">
                    <div className="whitespace-pre-wrap">
                      {campaign?.messageBody || 'N/A'}
                    </div>
                  </TabsContent>
                  <TabsContent value="html">
                    {campaign?.html && (
                      <PreviewEmailHtml html={campaign.html} />
                    )}
                    {!campaign?.html && 'N/A'}
                  </TabsContent>
                </Tabs>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="my-4">
        <div>
          <h1 className="mb-2 text-2xl font-bold">
            Recipients: {messageRecipients.length}
          </h1>
        </div>
        <Card>
          <CardContent>
            <div className="flex w-full flex-col gap-4">
              <div className="flex items-center">
                <span>Segments:</span>
                <div className="flex px-8">
                  {messageSegments.length
                    ? messageSegments?.map((segment) => (
                        <div
                          key={segment.id}
                          className="my-1 mr-2 inline-block overflow-hidden rounded-full bg-[#CAAB6A] px-2 py-1 text-center text-xs font-medium text-black"
                        >
                          {segment.name}
                        </div>
                      ))
                    : 'N/A'}
                </div>
              </div>
              <div className="flex w-full flex-col gap-2">
                <FilterBar>
                  {filters.map((f) => {
                    const def = filterDefinitions.find(
                      (d) => d.field === f.field,
                    );
                    if (!def) return null;
                    return (
                      <FilterEditor
                        key={f.field}
                        field={f.field}
                        displayName={f.displayName}
                        filterItem={f}
                        inputType={def.type}
                        operators={def.operators}
                        applyFilter={addOrUpdateFilter}
                        removeFilter={removeFilter}
                        description={def.description}
                      />
                    );
                  })}

                  <AddFilterTrigger
                    availableFilters={filterDefinitions.filter(
                      (def) => !filters.some((f) => f.field === def.field),
                    )}
                    onAdd={(newFilter) => addOrUpdateFilter(newFilter)}
                  />
                </FilterBar>
                <div className="flex items-center gap-4">
                  <Input
                    placeholder="Filter by first name..."
                    value={
                      filters.find((f) => f.field === 'firstName')?.value ?? ''
                    }
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value) {
                        addOrUpdateFilter({
                          field: 'firstName',
                          operator: 'contains',
                          value,
                          displayName: 'First Name',
                          displayValue: value,
                        });
                      } else {
                        removeFilter('firstName');
                      }
                    }}
                    className="w-64"
                  />

                  <Input
                    placeholder="Filter by email..."
                    value={
                      filters.find((f) => f.field === 'email')?.value ?? ''
                    }
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value) {
                        addOrUpdateFilter({
                          field: 'email',
                          operator: 'contains',
                          value,
                          displayName: 'Email',
                          displayValue: value,
                        });
                      } else {
                        removeFilter('email');
                      }
                    }}
                    className="w-64"
                  />

                  <Input
                    placeholder="Filter by phone number..."
                    value={
                      filters.find((f) => f.field === 'phoneNumber')?.value ??
                      ''
                    }
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value) {
                        addOrUpdateFilter({
                          field: 'phoneNumber',
                          operator: 'contains',
                          value,
                          displayName: 'Phone Number',
                          displayValue: value,
                        });
                      } else {
                        removeFilter('phoneNumber');
                      }
                    }}
                    className="w-64"
                  />
                </div>
                <div className="flex w-full flex-col rounded-md border-1">
                  <DataTable
                    table={table}
                    onRowClick={(row) => {
                      const memberOrFish =
                        getSubscriptionStatus(row.original) === 'Active'
                          ? 'members'
                          : 'fish';
                      navigate(`/${memberOrFish}/${row.original.id}`);
                    }}
                    isLoading={status === 'pending'}
                    skeletonRowCount={pageSize}
                  />
                  <DataTablePagination
                    table={table}
                    meta={{ total: table.getFilteredRowModel()?.rows?.length }}
                    pagination={{ size: pageSize, number: pageIndex + 1 }}
                    setPage={({ size, number }) => {
                      setPageSize(size);
                      setPageIndex(number - 1);
                    }}
                    setPageNumber={(n) => setPageIndex(n - 1)}
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <ModalLg>
        {modalContent === 'edit' && (
          <>
            <h2 className="mb-4 text-lg font-bold">Edit Campaign</h2>
            <OutboundMessageForm
              defaultValues={campaign}
              defaultRecipients={formattedRecipients}
              buttonText="Save Changes"
              onSubmitHandler={handleSubmitEdits}
              campaign={true}
              resetTable={() => {}}
              isEdit
            />
          </>
        )}

        {modalContent === 'delete' && (
          <div className="text-center">
            <p className="mb-4 text-lg">
              Are you sure you want to delete this message?
            </p>
            <div className="flex justify-center space-x-4">
              <Button
                onClick={confirmDelete}
                className="rounded bg-red-500 px-4 py-2 text-white hover:bg-red-600"
              >
                Confirm
              </Button>
              <Button
                onClick={closeModal}
                className="rounded bg-gray-300 px-4 py-2 text-gray-800 hover:bg-gray-400"
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </ModalLg>
    </div>
  );
};

export default CampaignDetails;
